import React, { useCallback, useMemo } from 'react'

import { EMPTY_OBJECT } from '@src/quince-site-common/src/constants/app'
import ProductComparisonBrand from '@src/quince-site-common/src/domains/ProductComparisonBrand'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import productHelper, {
  CUSTOM_FIELD_NAMES,
  isGiftCardCreditSupported,
  isProductVitamin,
} from '@src/quince-site-common/src/helpers/productHelper'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { useTrackIsElementVisible } from '@src/quince-site-common/src/hooks/useTrackIsElementVisible'
import pdpStateManager from '@src/quince-site-common/src/redux/globalStateManager/PDPStateManager'
import { useAppSelector } from '@src/quince-site-common/src/redux/hooks'
import { pick } from '@src/utils/functions/utils'

import ProductPageSectionProps from '../../../domains/ProductPageSectionProps'
import ProductPriceComparison from '../../../molecules/productPriceComparision'

import styles from './brandComparision.module.scss'
import { getProductComparisionBrands } from './BrandComparison'
import { shouldHideFreeReturns } from './brandComparison.helpers'

const GIFT_CREDIT_KEYS = ['giftCardCredit', 'savings']

const BrandComparisonProduct = ({ product }: ProductPageSectionProps) => {
  const selectedOptions = useAppSelector<ProductVariantOption[]>(pdpStateManager, pdpStateManager.getSelectedOptions)
  const deviceInfo = useDeviceInfo()
  const isMobile = deviceInfo.deviceType !== DEVICE_TYPES.DESKTOP
  const productComparisionsBrands: ProductComparisonBrand[] = getProductComparisionBrands(
    product,
    selectedOptions,
    isMobile,
  )

  const selectedVariant = useMemo(
    () => productHelper.getVariantsForOptions(product, selectedOptions)[0],
    [product, selectedOptions],
  )

  const getIsVisiblePriceCompareTrackParams = useCallback(() => {
    const productId = product.id
    const selectedColor = selectedOptions.filter(o => o.name == 'Color')[0]?.value
    return {
      action: 'brand_comparision',
      value: true,
      context: {
        productId,
        selectedColor,
      },
    }
  }, [product.id, selectedOptions.filter(o => o.name == 'Color')[0]?.value])

  const { ref: trackIsVisiblePriceCompareRef } = useTrackIsElementVisible(getIsVisiblePriceCompareTrackParams)

  const giftCardInformation = useMemo(
    () => ({
      ...pick(selectedVariant || EMPTY_OBJECT, GIFT_CREDIT_KEYS),
      productComparisionsBrands: productComparisionsBrands.map(brand => ({
        ...brand,
        prices: brand.prices.filter(price => price.variantId === selectedVariant.id),
      })),
    }),
    [selectedVariant, productComparisionsBrands],
  )

  if (
    productComparisionsBrands.length === 0 ||
    !!product.hideBrandComparison ||
    productHelper.getIsTruthyCustomField(product, CUSTOM_FIELD_NAMES.HIDE_COMP_PRICING)
  )
    return null

  const servings = productHelper.getServingsForOptions(product, selectedOptions)
  const isVitamin = isProductVitamin(product)
  const pricePerServing = selectedVariant?.pricePerServing

  return (
    <div className={styles.brandComparison} ref={trackIsVisiblePriceCompareRef}>
      <ProductPriceComparison
        quincePrice={productHelper.getQuincePriceForOptions(product, selectedOptions)}
        productComparisionsBrands={productComparisionsBrands}
        hideFreeReturns={shouldHideFreeReturns(product)}
        hasRestockingFee={product.hasRestockingFee}
        showDollarComparisonPrice={false}
        showGiftCardCredit={isGiftCardCreditSupported(selectedVariant)}
        giftCardInformation={giftCardInformation}
        servings={servings}
        isVitamin={isVitamin}
        pricePerServing={pricePerServing}
      />
    </div>
  )
}

export default BrandComparisonProduct
